import React, { useEffect, useReducer, useRef } from 'react';

import _ from 'lodash';
import { IconButton, LinearProgress, Paper } from '@material-ui/core';
import { Table as MuiTable, TableBody, TableFooter, TableHead } from '@material-ui/core';
import { TableCell, TableRow } from '@material-ui/core';
import { TablePagination, TableSortLabel, Typography } from '@material-ui/core';
import { MdRefresh } from 'react-icons/md';

import { textSort } from '.';
import { SearchField, Tooltip } from '..';
import { randomKey, classes } from 'utils';
import { initialState, parseColumnConfig, sortQuery } from './helpers';
import TablePaginationActions from './TablePaginationActions';

import styles from './Table.module.scss';

const reducer = (state, action) => {
  switch (action.type) {
    case 'SORT': {
      const { field: orderBy, prefOrder } = action.payload;

      let orderDirection;

      if (orderBy !== state.orderBy) {
        if (prefOrder) {
          orderDirection = prefOrder === 'asc' ? 'asc' : 'desc';
        } else {
          orderDirection = 'asc';
        }
      } else {
        orderDirection = state.orderDirection === 'desc' ? 'asc' : 'desc';
      }

      return {
        ...state,
        orderBy,
        orderDirection,
        sort: sortQuery(orderBy, orderDirection),
      };
    }

    case 'SEARCH':
      return { ...state, search: action.payload || undefined };

    case 'FILTER': {
      const { field, value, type = 'text', dependents } = action.payload;
      const filter = { ...state.filter };

      switch (type) {
        case 'text':
          if (value || typeof value === 'boolean') {
            filter[field] = value;
          } else {
            delete filter[field];
          }

          if (Array.isArray(dependents) && dependents.length) {
            dependents.forEach(it => delete filter[it]);
          }

          break;

        default:
          break;
      }

      return _.isEqual(state.filter, filter) ? state : { ...state, filter };
    }

    case 'PAGINATION':
      return { ...state, ...action.payload };

    case 'LOADING':
      return state.isLoading && state.session === action.payload
        ? state
        : { ...state, isLoading: true, session: action.payload };

    case 'DATA': {
      const payload = { data: [], page: 0, count: 0, ...(action.payload || {}) };

      return !state.session || state.session === payload.session
        ? { ...state, ...payload, isLoading: false, session: null }
        : state;
    }

    case 'RELOAD':
      return { ...state, refresh: ((state.refresh || 0) + 1) % 2, ...action.payload };

    default:
      return state;
  }
};

const Table = props => {
  const [state, dispatch] = useReducer(reducer, initialState(props));

  const instance = useRef({
    reload: (reset = true) =>
      dispatch({ type: 'RELOAD', payload: reset ? { page: 0 } : undefined }),
  });

  const columns = parseColumnConfig(props.columns);
  const lastFilter = useRef(state.filter);

  instance.current.state = state;
  instance.current.columns = columns;

  instance.current.settings = _.pick(state, [
    'filter',
    'orderBy',
    'orderDirection',
    'page',
    'pageSize',
  ]);

  useEffect(() => {
    if (typeof props.tableRef === 'function') {
      props.tableRef(instance.current);
    } else if (typeof props.tableRef === 'object') {
      props.tableRef.current = instance.current;
    }
  }, [props.tableRef]);

  const { page, pageSize, count, sort, search, isLoading, refresh, filter } = state;

  useEffect(() => {
    if (typeof props.data === 'function') {
      const session = randomKey(10);
      dispatch({ type: 'LOADING', payload: session });

      const asyncFetch = async () => {
        const payload = { session };

        try {
          const filterChanged = !_.isEqual(filter, lastFilter.current);
          lastFilter.current = filter;

          const { status, data } = await props.data({
            page: filterChanged ? 1 : page + 1,
            pageSize,
            sort,
            search,
            filter,
          });

          if (Math.floor(status / 100) === 2 && data) {
            payload.data = data.docs;
            payload.page = data.page - 1;
            payload.count = data.total;
          }
        } catch (err) {
          console.error(err);
        } finally {
          dispatch({ type: 'DATA', payload });
        }
      };

      asyncFetch();
    } else if (Array.isArray(props.data)) {
      const data = [...props.data];

      const {
        state: { orderBy, orderDirection },
        columns,
      } = instance.current;

      if (orderBy) {
        const sortColumn = columns.find(it => it.field === orderBy);

        if (sortColumn) {
          const mult = orderDirection === 'asc' ? 1 : -1;
          const sortFunc = sortColumn.customSort || textSort(orderBy);

          data.sort((a, b) => sortFunc(a, b) * mult);
        }
      }

      dispatch({
        type: 'DATA',
        payload: {
          data: data.slice(page * pageSize, (page + 1) * pageSize),
          page,
          count: data.length,
        },
      });
    }
  }, [sort, search, page, pageSize, refresh, filter, props.data]);

  const renderBody = () => {
    const { data, filter = {} } = state;
    const rows = [];

    if (props.filtering) {
      rows.push(
        <TableRow key="__filter__">
          {columns.map(({ key, field, filtering = true, dependents }) => {
            if (filtering && typeof filtering !== 'object') {
              filtering = { type: 'text' };
            }

            return (
              <TableCell key={key}>
                {filtering ? (
                  <SearchField
                    filter
                    value={filter[field]}
                    type={filtering.type}
                    options={filtering.options}
                    optionsParams={_.pick(filter, filtering.depends)}
                    onChange={value =>
                      dispatch({ type: 'FILTER', payload: { field, value, dependents } })
                    }
                  />
                ) : null}
              </TableCell>
            );
          })}
        </TableRow>
      );
    }

    if (Array.isArray(data) && data.length) {
      if (state.orderBy) {
        const sortColumn = columns.find(it => it.field === state.orderBy);

        if (sortColumn && sortColumn.customSort) {
          const mult = state.orderDirection === 'asc' ? 1 : -1;
          data.sort((a, b) => sortColumn.customSort(a, b) * mult);
        }
      }

      data.forEach(row => {
        rows.push(
          <TableRow
            key={row._id || row.id}
            onClick={props.onRowClick ? () => props.onRowClick(row) : null}
            style={typeof props.rowStyle === 'function' ? props.rowStyle(row) : undefined}
            selected={(row._id || row.id) === props.selected}
          >
            {columns.map(({ key, field, render, cellStyle }) => {
              let children;

              if (render) {
                children = render(row, lastFilter.current);
              } else {
                children = _.get(row, field);

                if (typeof children !== 'string' && !_.isNil(children)) {
                  children = String(children);
                }
              }

              return (
                <TableCell key={key} style={cellStyle}>
                  {children}
                </TableCell>
              );
            })}
          </TableRow>
        );
      });
    } else {
      rows.push(
        <TableRow key="_no_records_">
          <TableCell colSpan={columns.length || 1} style={{ textAlign: 'center' }}>
            <Typography
              variant="caption"
              style={isLoading ? { visibility: 'hidden' } : undefined}
            >
              No records to display
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    if (isLoading) {
      rows.push(
        <TableRow key="_progress_" style={{ opacity: 1 }}>
          <TableCell
            colSpan={columns.length || 1}
            style={{ minHeight: rows.length ? 0 : 64, padding: 0 }}
          >
            <LinearProgress />
          </TableCell>
        </TableRow>
      );
    }

    return rows;
  };

  const hasTopBar =
    props.title || props.search || props.refreshButton || props.controlPanel;

  return (
    <Paper
      className={classes(styles.table, isLoading ? styles.loading : '')}
      style={props.style}
    >
      <div
        className={styles.topbar}
        style={hasTopBar ? props.topBarStyle || undefined : { display: 'none' }}
      >
        <div className={styles.title}>
          <Typography variant="h6">{props.title}</Typography>

          <div style={{ display: 'flex' }}>
            {props.search !== false ? (
              <div className={styles.search}>
                <SearchField
                  onChange={payload => dispatch({ type: 'SEARCH', payload })}
                />
              </div>
            ) : null}

            {props.refreshButton ? (
              <IconButton
                color="primary"
                onClick={() => instance.current.reload()}
                disabled={isLoading}
              >
                <Tooltip title="Refresh" placement="top">
                  <MdRefresh />
                </Tooltip>
              </IconButton>
            ) : null}
          </div>
        </div>

        {props.controlPanel}
      </div>

      <MuiTable>
        <TableHead>
          <TableRow>
            {columns.map(it => (
              <TableCell key={it.key} style={it.headerStyle}>
                {it.sorting !== false ? (
                  <TableSortLabel
                    active={state.orderBy === it.field}
                    direction={state.orderDirection}
                    onClick={() =>
                      dispatch({
                        type: 'SORT',
                        payload: { field: it.field, prefOrder: it.initialSort },
                      })
                    }
                  >
                    {it.title}
                  </TableSortLabel>
                ) : (
                  it.title
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>{renderBody()}</TableBody>

        <TableFooter>
          <TableRow>
            <TablePagination
              page={page}
              count={count}
              rowsPerPage={pageSize}
              labelDisplayedRows={() => null}
              rowsPerPageOptions={props.pageSizeOptions || [5, 10, 20]}
              onPageChange={(_, page) =>
                dispatch({ type: 'PAGINATION', payload: { page } })
              }
              onRowsPerPageChange={e =>
                dispatch({
                  type: 'PAGINATION',
                  payload: { page: 0, pageSize: parseInt(e.target.value) },
                })
              }
              ActionsComponent={TablePaginationActions}
              style={isLoading ? undefined : { paddingTop: 5 }}
            />
          </TableRow>
        </TableFooter>
      </MuiTable>
    </Paper>
  );
};

export default Table;
